import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import type { TeamSelection } from '@/lib/team-pricing';
import {
  initIAPConnection,
  closeIAPConnection,
  requestAppleSubscription,
  requestAppleProduct,
  validateApplePurchaseWithBackend,
  validateAppleTeamPurchaseWithBackend,
  restoreApplePurchases,
  purchaseUpdatedListener,
  purchaseErrorListener,
  isAppleIAPAvailable,
  getPlanIdForProductId,
  ErrorCode,
  type AppleIAPPurchaseResult,
} from '@/services/appleIapService';
import {
  isGooglePlayAvailable,
  requestGoogleSubscription,
  restoreGooglePurchases,
  validateGooglePurchaseWithBackend,
} from '@/services/googlePlayIapService';
import type { Purchase } from 'expo-iap';
import { sanitizeErrorText } from '@/utils/api-error';

export type AppleIAPState =
  | 'idle'
  | 'connecting'
  | 'purchasing'
  | 'validating'
  | 'success'
  | 'error';

interface UseAppleIAPOptions {
  onSuccess?: (result: AppleIAPPurchaseResult) => void;
  onError?: (message: string) => void;
}

export interface UseAppleIAPReturn {
  state: AppleIAPState;
  error: string | null;
  purchaseSubscription: (planId: string) => Promise<void>;
  /** Degrau da escada de equipe, com o que o dono escolheu dentro dele. */
  purchaseTeamProduct: (productId: string, selection: TeamSelection) => Promise<void>;
  restorePurchases: () => Promise<boolean>;
  isAvailable: boolean;
}

/**
 * Safety net for the purchase flow: StoreKit delivers purchase results via listener,
 * which can silently never fire (e.g. a stale unfinished transaction blocking the
 * new request). Without this, the UI would be stuck on a loading state forever.
 */
const PURCHASE_TIMEOUT_MS = 30000;

/**
 * Compra de assinatura pela loja do aparelho: App Store no iPhone, Google Play
 * no Android (o plano de equipe só existe na Apple). Abre e fecha a conexão
 * com a loja sozinho.
 */
export function useAppleIAP({ onSuccess, onError }: UseAppleIAPOptions = {}): UseAppleIAPReturn {
  const [state, setState] = useState<AppleIAPState>('idle');
  const [error, setError] = useState<string | null>(null);

  const { fetchProfile } = useAuthStore();
  const queryClient = useQueryClient();

  const isGoogle = isGooglePlayAvailable();
  const isAvailable = isAppleIAPAvailable() || isGoogle;
  const connectedRef = useRef(false);
  const activePlanIdRef = useRef<string | null>(null);
  const activeTeamRef = useRef<{ productId: string; selection: TeamSelection } | null>(null);
  const purchaseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPurchaseTimeout = useCallback(() => {
    if (purchaseTimeoutRef.current) {
      clearTimeout(purchaseTimeoutRef.current);
      purchaseTimeoutRef.current = null;
    }
  }, []);

  const clearActive = useCallback(() => {
    activePlanIdRef.current = null;
    activeTeamRef.current = null;
  }, []);

  useEffect(() => {
    if (!isAvailable) return;

    let cancelled = false;

    const connect = async () => {
      setState('connecting');
      const ok = await initIAPConnection();
      if (!cancelled) {
        connectedRef.current = ok;
        setState('idle');
      }
    };

    connect();

    return () => {
      cancelled = true;
      closeIAPConnection();
      connectedRef.current = false;
    };
  }, [isAvailable]);

  useEffect(() => {
    if (!isAvailable) return;

    const purchaseSub = purchaseUpdatedListener(async (purchase: Purchase) => {
      if (purchase.purchaseState !== 'purchased') return;

      // A purchase can be redelivered by StoreKit on connect (e.g. a transaction
      // left unfinished by a previous attempt) without the user selecting a plan
      // in this session. Fall back to the product's mapped plan so it still gets
      // validated and finished instead of being silently dropped forever. A
      // product outside the solo map is a team ladder step.
      const team = activeTeamRef.current;
      const isUserInitiated = activePlanIdRef.current != null || team != null;
      const planId = activePlanIdRef.current ?? getPlanIdForProductId(purchase.productId);

      if (isUserInitiated) {
        clearPurchaseTimeout();
        setState('validating');
      }

      const result = isGoogle
        ? await validateGooglePurchaseWithBackend(purchase)
        : planId
          ? await validateApplePurchaseWithBackend(purchase, planId)
          : await validateAppleTeamPurchaseWithBackend(
              purchase,
              team?.productId === purchase.productId ? team.selection : null,
            );

      if (result.ok) {
        await fetchProfile();
        queryClient.invalidateQueries({ queryKey: ['provider'] });
        queryClient.invalidateQueries({ queryKey: ['team'] });
        if (isUserInitiated) {
          setState('success');
          onSuccess?.(result);
        }
      } else if (isUserInitiated) {
        const msg = result.error ?? 'Falha ao validar a compra.';
        setError(msg);
        setState('error');
        onError?.(msg);
      }

      clearActive();
    });

    const errorSub = purchaseErrorListener((err) => {
      clearPurchaseTimeout();

      if (err.code === ErrorCode.UserCancelled) {
        setState('idle');
        clearActive();
        return;
      }

      const msg = sanitizeErrorText(err.message, 'Erro ao processar compra.');
      setError(msg);
      setState('error');
      clearActive();
      onError?.(msg);
    });

    return () => {
      purchaseSub.remove();
      errorSub.remove();
      clearPurchaseTimeout();
    };
  }, [
    isAvailable,
    isGoogle,
    fetchProfile,
    queryClient,
    onSuccess,
    onError,
    clearPurchaseTimeout,
    clearActive,
  ]);

  /** Arma o timeout e dispara a compra; o resultado chega pelo listener. */
  const startPurchase = useCallback(
    async (isStillActive: () => boolean, request: () => Promise<void>) => {
      setError(null);
      setState('purchasing');

      clearPurchaseTimeout();
      purchaseTimeoutRef.current = setTimeout(() => {
        if (!isStillActive()) return;
        const msg = 'A compra demorou para responder. Tente novamente ou use "Restaurar compras".';
        setError(msg);
        setState('error');
        clearActive();
        onError?.(msg);
      }, PURCHASE_TIMEOUT_MS);

      try {
        await request();
        // Result delivered via purchaseUpdatedListener
      } catch (err: unknown) {
        clearPurchaseTimeout();
        const msg = sanitizeErrorText(err instanceof Error ? err.message : null, 'Erro ao iniciar compra.');
        setError(msg);
        setState('error');
        clearActive();
        onError?.(msg);
      }
    },
    [onError, clearPurchaseTimeout, clearActive],
  );

  const purchaseSubscription = useCallback(
    async (planId: string) => {
      if (!isAvailable) return;
      activeTeamRef.current = null;
      activePlanIdRef.current = planId;
      await startPurchase(
        () => activePlanIdRef.current === planId,
        () => {
          if (!isGoogle) return requestAppleSubscription(planId);
          // Já assina pelo Google: troca o plano dessa assinatura.
          const provider = useAuthStore.getState().user?.providerProfile;
          return requestGoogleSubscription(planId, {
            userId: useAuthStore.getState().user?.id,
            currentPurchaseToken:
              provider?.subscriptionPaymentMethod === 'google_play' &&
              provider.subscriptionStatus !== 'canceled'
                ? provider.subscriptionId
                : null,
          });
        },
      );
    },
    [isAvailable, isGoogle, startPurchase],
  );

  const purchaseTeamProduct = useCallback(
    async (productId: string, selection: TeamSelection) => {
      if (!isAvailable || isGoogle) return;
      activePlanIdRef.current = null;
      activeTeamRef.current = { productId, selection };
      await startPurchase(
        () => activeTeamRef.current?.productId === productId,
        () => requestAppleProduct(productId),
      );
    },
    [isAvailable, isGoogle, startPurchase],
  );

  const restorePurchases = useCallback(async (): Promise<boolean> => {
    if (!isAvailable) return false;

    setError(null);
    setState('validating');

    try {
      const results = isGoogle ? await restoreGooglePurchases() : await restoreApplePurchases();
      const restored = results.some((result) => result.ok);

      if (restored) {
        await fetchProfile();
        queryClient.invalidateQueries({ queryKey: ['provider'] });
        setState('success');
        onSuccess?.(results.find((result) => result.ok)!);
      } else {
        setState('idle');
      }

      return restored;
    } catch (err: unknown) {
      const msg = sanitizeErrorText(err instanceof Error ? err.message : null, 'Erro ao restaurar compras.');
      setError(msg);
      setState('error');
      onError?.(msg);
      return false;
    }
  }, [isAvailable, isGoogle, fetchProfile, queryClient, onSuccess, onError]);

  return {
    state,
    error,
    purchaseSubscription,
    purchaseTeamProduct,
    restorePurchases,
    isAvailable,
  };
}
